import { Archive, Copy, PauseCircle, Pencil, Play, Trash2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { StatusPill } from '@/components/app';
import { PricingVariant } from '../../../api/pricingVariants';
import { VARIANT_STATUS_LABELS, variantStatusTone } from '../variantStatus';
import { durationLabel, formatMoney } from '../../Plans/planFormat';
import { labelOf } from '@/lib/labels';

interface VariantsListProps {
  variants: PricingVariant[];
  onEdit: (variant: PricingVariant) => void;
  onClonePlans: (variant: PricingVariant) => void;
  onActivate: (variant: PricingVariant) => void;
  onStopEnrollment: (variant: PricingVariant) => void;
  onArchive: (variant: PricingVariant) => void;
  onDelete: (variant: PricingVariant) => void;
}

export function VariantsList({
  variants,
  onEdit,
  onClonePlans,
  onActivate,
  onStopEnrollment,
  onArchive,
  onDelete,
}: VariantsListProps) {
  if (variants.length === 0) {
    return null;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {variants.map((variant) => {
        const isArchived = variant.status === 'archived';
        const plans = variant.plans ?? [];

        return (
          <Card
            key={variant.id}
            role="region"
            aria-label={`Вариант «${variant.name}»`}
            className={cn('flex flex-col gap-4 p-[18px]', isArchived && 'opacity-60')}
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="m-0 font-display text-[17px] font-bold">{variant.name}</h3>
                <StatusPill size="sm" tone={variantStatusTone(variant.status)}>
                  {labelOf(VARIANT_STATUS_LABELS, variant.status)}
                </StatusPill>
                {variant.is_control && (
                  <StatusPill size="sm" tone="info">
                    контрольный
                  </StatusPill>
                )}
              </div>
              <p className="m-0 mt-1 text-[13px] text-text-2">
                Вес набора: <span className="tabular-nums">{variant.weight}</span>
                {variant.activated_at && (
                  <>
                    {' '}· запущен{' '}
                    <span className="tabular-nums">{new Date(variant.activated_at).toLocaleDateString('ru-RU', { timeZone: 'Asia/Tashkent' })}</span>
                  </>
                )}
              </p>
              {variant.description && <p className="m-0 mt-2 text-sm text-text-2">{variant.description}</p>}
            </div>

            {/* Прайс-лист варианта */}
            <div className="flex flex-col gap-1.5">
              {plans.length === 0 ? (
                <p className="m-0 rounded-xl bg-warn-soft px-3 py-2 text-sm font-semibold text-warn">
                  Тарифов нет — вариант нельзя запустить
                </p>
              ) : (
                plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={cn(
                      'flex items-center justify-between gap-3 rounded-xl bg-surface-alt px-3 py-2 text-sm',
                      !plan.is_active && 'opacity-50',
                    )}
                  >
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <code className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs">{plan.code}</code>
                      <span className="text-text-2">{durationLabel(plan.duration_value, plan.duration_unit)}</span>
                      {!plan.is_active && <span className="text-xs text-text-2">· скрыт</span>}
                    </div>
                    <span className="whitespace-nowrap font-display font-bold tabular-nums">
                      {formatMoney(plan.price, plan.currency)}
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* Действия */}
            <div className="mt-auto flex flex-wrap items-center gap-2">
              {variant.status !== 'active' && !isArchived && (
                <Button size="sm" onClick={() => onActivate(variant)}>
                  <Play />
                  Запустить набор
                </Button>
              )}

              {variant.status === 'active' && (
                <Button variant="secondary" size="sm" onClick={() => onStopEnrollment(variant)}>
                  <PauseCircle />
                  Стоп набора
                </Button>
              )}

              {!isArchived && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => onEdit(variant)}>
                    <Pencil />
                    Изменить
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onClonePlans(variant)}>
                    <Copy />
                    Скопировать тарифы
                  </Button>
                </>
              )}

              {!isArchived && !variant.is_control && (
                <Button variant="destructiveOutline" size="sm" onClick={() => onArchive(variant)}>
                  <Archive />
                  Архив
                </Button>
              )}

              {/* Удаление разрешает бэкенд: только если варианту никого не назначили
                  и по его тарифам не было заказов. Иначе вернётся понятная ошибка. */}
              {!isArchived && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Удалить вариант «${variant.name}»`}
                  onClick={() => onDelete(variant)}
                  className="ml-auto text-text-2 hover:bg-neg-soft hover:text-neg"
                >
                  <Trash2 />
                </Button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
